/*
 * COPYRIGHT (c) 2022
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  formattedDate,
  getFirstDayofSelectedMonth,
  isObjectOrArrayAndEmpty
} from '../../../shared/Utils/Utils';
import { getConnctorConfig } from '../../../shared/Utils/DataConnectorUtils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import ICarbonEmissionHOCProps from './ICarbonEmissionHOCProps';
import useMounted from '../../../shared/useMount/useMount';
import CarbonComponentSite from '../CarbonComponent/CarbonComponentSite';
import './CarbonEmissionHOC.scss';
import {
  ECarbonCartAt,
  IAny,
  ICarbonData,
  ISiteCard
} from '../../IEnergyManagementProps';
import { getCarbonEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import TooltipError from '../../../../images/tooltipError.svg';
import { ApiContext } from '../../../../context/ApiContext';
import { Tooltip } from '@scuf/common';
interface ICarbonEmissionHOCContent extends ISiteCard {
  data: ICarbonData;
  toggleFeatureScopeOne: boolean | undefined;
}

const DataAvailableCheck = (dataAvailableCheck: IAny) =>
  dataAvailableCheck ? true : false;

const CarbonEmissionHOCContent = (props: ICarbonEmissionHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, toggleFeatureScopeOne } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <CarbonComponentSite
      data={data}
      toggleFeatureScopeOne={toggleFeatureScopeOne}
    />
  );
};

const CarbonEmmisionHOCWithLoader = withLoadingStatus(
  withErrorStatus(CarbonEmissionHOCContent)
);

const getConnectorConfigName = (toggleFeatureScopeOne: boolean | undefined) => {
  return toggleFeatureScopeOne ? 'site-carbon-new' : 'Co2-carbonEmission';
};

const CarbonEmissionHOCSite = (props: ICarbonEmissionHOCProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;
  const { bearerToken, requestContext, carbonApiFor, dataSourceType } = props;
  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime = getCarbonEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const endTimeNewFormat = formattedDate(endTime);

  const connectorConfig =
    carbonApiFor === ECarbonCartAt.siteLabel
      ? getConnctorConfig(
          getConnectorConfigName(toggleFeatureScopeOne),
          requestContext,
          startTime,
          endTimeNewFormat,
          { dataSource: '' }
        )
      : {
          name: getConnectorConfigName(toggleFeatureScopeOne),
          options: {
            startDate: startTime,
            endDate: endTimeNewFormat,
            allDetails: 'true',
            dataSource: ''
          }
        };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });
  const connectorConfigMissigData = {
    name: 'get-missing-data-details-forPeriod',
    options: {
      startDate: startTime,
      endDate: endTime,
      dataSource: dataSourceType || ''
    }
  };
  const [dataConnectorMissingData] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigMissigData,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      dataConnector?.actions?.getAll();
      dataConnectorMissingData?.actions?.getAll();
    }
  }, [requestContext, dataSourceType]);

  const onRefresh = () => dataConnector?.actions?.getAll();
  const { all, loading, error, status } = dataConnector.data;

  const {
    all: allMissingData,
    loading: loadingMissingData,
    error: errorMissingData,
    status: statusMissingData
  } = dataConnectorMissingData.data;

  const MissingDataCondition = allMissingData as IAny;
  const isMissingDataAvailable = DataAvailableCheck(MissingDataCondition);
  const CarbonEmissionSavings = (all as unknown) as ICarbonData;
  const isDataAvailable = DataAvailableCheck(CarbonEmissionSavings);

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonData);

  const [noDataCount, setnoDataCount] = useState<number>(0);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(CarbonEmissionSavings);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    if (
      (isMissingDataAvailable || errorMissingData || statusMissingData) &&
      !loadingMissingData
    ) {
      setnoDataCount(MissingDataCondition.totalAlerts);
    }
  }, [isMissingDataAvailable, loadingMissingData, statusMissingData]);

  return (
    <>
      {carbonApiFor === ECarbonCartAt.siteLabel &&
        noDataCount > 0 &&
        toggleFeatureScopeOne && (
          <div className="errorToolTipDisplay">
            <Tooltip
              element={<img className="alarm-meter-icon" src={TooltipError} />}
              event="hover"
              content={`${t(d.DATANEEDEDCONTENT)}`}
              position="bottom center"
              size="small"
              className="dataNeeded"
            />
            {/* <img className="alarm-meter-icon" src={TooltipError} /> */}
            <div className="kpi-card-title-one">{t(d.DATANEEDED)}</div>
          </div>
        )}
      <CarbonEmmisionHOCWithLoader
        loading={isLoading}
        error={errorInfo}
        statusCode={statusCode}
        onRefresh={onRefresh}
        data={data}
        loadingText={t(d.LOADING)}
        title={t(d.CONNECTIONERRORTITLE)}
        noDataMessage={t(d.NODATAAVAILABLE)}
        connectionErrorMessage={t(d.CONNECTIONERROR)}
        toggleFeatureScopeOne={toggleFeatureScopeOne}
      />
    </>
  );
};
export default CarbonEmissionHOCSite;
